import { Component, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges } from '@angular/core';
import { DynamicSpecialFunctionType, OrderDialogItem, OrderheadService, SellerItem } from '@app/services/orderhead.service';
import { OrderDialogEvent } from '../orderhead-dialog/orderhead-dialog.component';
import { PostPlaceItem, TypeaheadService } from '@app/services/typeahead.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { OrderHeadContextService, OrderHeadDynamicSpecialOrderHead } from '../order-head/order-head-context.service';
import { Subject, takeUntil } from 'rxjs';
import { CoopItem, StockplaceItem } from '@app/services/logistic.service';
import { CustomerPersonItem } from '@app/services/economy.service';
import { SpecialDiscount2Item } from '@app/services/discount-group.service';

@Component({
    selector: 'tt-order-head-dynamic-special',
    templateUrl: './order-head-dynamic-special.component.html',
    styleUrls: ['./order-head-dynamic-special.component.css'],
})
export class OrderHeadDynamicSpecialComponent implements OnChanges, OnDestroy {
    @Input()
    orderhead?: OrderHeadDynamicSpecialOrderHead;

    @Output()
    orderheadChange = new EventEmitter<OrderHeadDynamicSpecialOrderHead>();

    @Input()
    dialog: OrderDialogItem[] = [];

    @Input()
    coops: CoopItem[] = [];

    @Input()
    sellers: SellerItem[] = [];

    @Input()
    customerPersons: CustomerPersonItem[] = [];

    @Input()
    specialDiscount2: SpecialDiscount2Item[] = [];

    @Input()
    stockplaces: StockplaceItem[] = [];

    @Input()
    postplaceSearch: (value: string) => Promise<DynamicSpecialPostplaceItem[]> = async () => [];

    @Input()
    public showSaveButton = true;

    @Output() // The kendo-editor is very slow, and so it cannot be used in javascript component, so while the orderhead page component is written in js it needs to use the js modal component, thus an event is emitted.
    public openDialog = new EventEmitter<OrderDialogEvent>();

    @Output()
    public dynamicSpecialChange = new EventEmitter<OrderHeadDynamicSpecialChangeEvent>();

    @Output()
    public dynamicSpecialSave = new EventEmitter<unknown>();

    public saving = false;

    public confirming = false;

    public callPicklist = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private orderHeadService: OrderheadService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        @Optional() private context: OrderHeadContextService | null,
    ) {
        this.bindContext();
    }

    public async save() {
        this.saving = true;

        try {
            await this.callOrderheadDynamicSpecialFunctions('special_orderhead_construction_save');
        } finally {
            this.dynamicSpecialSave.emit();
            this.saving = false;
        }
    }

    public async picklist() {
        this.callPicklist = true;

        try {
            await this.callOrderheadDynamicSpecialFunctions('special_orderhead_construction_picklist');
        } finally {
            this.callPicklist = false;
        }
    }

    public async confirmOrder() {
        this.confirming = true;

        try {
            await this.callOrderheadDynamicSpecialFunctions('special_orderhead_construction_orderconfirm');
        } finally {
            this.confirming = false;
        }
    }

    public searchPostplaces = async (params?: { [key: string]: any }) => {
        const searchtext = params?.['value'] ?? this.orderhead?.delivery_postplace_id_street ?? '';

        if (!this.context) {
            return this.postplaceSearch(searchtext);
        }

        const postplaces = await this.typeahead.searchPostplaces({ searchtext });

        return postplaces.map((postplace) => this.toDynamicSpecialPostplace(postplace));
    };

    public onPostplaceSelected(item?: DynamicSpecialPostplaceItem) {
        if (!!this.orderhead) {
            this.orderhead.delivery_postplace_name_street = item?.postplace_name ?? '';
            this.orderhead.delivery_postplace_id_street = item?.postplace_id ?? '';

            this.onDynamicSpecialChange('delivery_postplace_id_street', 'delivery_postplace_name_street');
        }
    }

    public searchCustomer = () => {
        return this.typeahead.searchCustomer({ cust_name: this.orderhead?.stat_cust_name ?? '', custtype: 'C', lastselect: 'SEARCH' });
    };

    public onStatCustomerSelected(item?: Item) {
        if (!!this.orderhead) {
            this.orderhead.stat_cust_no = item?.item_id ?? '0';
            this.orderhead.stat_cust_name = item?.item_name ?? '';

            this.onDynamicSpecialChange('stat_cust_no', 'stat_cust_name');
        }
    }

    public onCustomerSelfPickupChange() {
        const fields: (keyof OrderHeadDynamicSpecialOrderHead)[] = ['customer_self_pickup'];

        if (this.orderhead?.deliver_to_customer_stock === '1') {
            this.orderhead.deliver_to_customer_stock = '0';
            fields.push('deliver_to_customer_stock');
        }

        this.onDynamicSpecialChange(...fields);
    }

    public onDeliverToCustomerStockChange() {
        const fields: (keyof OrderHeadDynamicSpecialOrderHead)[] = ['deliver_to_customer_stock'];

        if (this.orderhead?.customer_self_pickup === '1') {
            this.orderhead.customer_self_pickup = '0';
            fields.push('customer_self_pickup');
        }

        this.onDynamicSpecialChange(...fields);
    }

    public onConstruction06Change() {
        const fields: (keyof OrderHeadDynamicSpecialOrderHead)[] = ['special_construction06'];

        if (this.orderhead?.special_construction07 === '1') {
            this.orderhead.special_construction07 = '0';
            fields.push('special_construction07');
        }

        this.onDynamicSpecialChange(...fields);
    }

    public onConstruction07Change() {
        const fields: (keyof OrderHeadDynamicSpecialOrderHead)[] = ['special_construction07'];

        if (this.orderhead?.special_construction06 === '1') {
            this.orderhead.special_construction06 = '0';
            fields.push('special_construction06');
        }

        this.onDynamicSpecialChange(...fields);
    }

    public onDynamicSpecialChange(...fields: (keyof OrderHeadDynamicSpecialOrderHead)[]) {
        if (!!this.orderhead) {
            this.updateContextListKeys(fields);
            this.orderheadChange.emit(this.orderhead);
            this.dynamicSpecialChange.emit({ fields: fields, data: this.orderhead });
        }
    }

    private async callOrderheadDynamicSpecialFunctions(specialFunction: DynamicSpecialFunctionType) {
        if (!!this.orderhead) {
            const response = await this.orderHeadService.callDynamicSpecialFunctions({ ...this.orderhead, special_function: specialFunction });

            if (Array.isArray(response) && this.isErrorMessageResponse(response[0]) && '' + response[0].errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response[0].errormessage });
            }
        }
    }

    private async loadDynamicSpecialOrderHead(orderInternalNo: string) {
        if (!!this.orderhead) {
            const data = await this.orderHeadService.getDyanmicSpecialOrderHead({ order_internal_no: orderInternalNo });

            if ('' + this.orderhead.order_internal_no === orderInternalNo) {
                this.orderhead = { ...data, ...this.orderhead };
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.context && changes['orderhead']) {
            const current = changes['orderhead'].currentValue as OrderHeadDynamicSpecialOrderHead | undefined;
            const previous = changes['orderhead'].previousValue as OrderHeadDynamicSpecialOrderHead | undefined;
            const currentOrderInternalNo = current?.order_internal_no ? '' + current.order_internal_no : '';
            const previousOrderInternalNo = previous?.order_internal_no ? '' + previous.order_internal_no : '';

            if (!!currentOrderInternalNo && currentOrderInternalNo !== previousOrderInternalNo) {
                this.loadDynamicSpecialOrderHead(currentOrderInternalNo);
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isErrorMessageResponse(value: unknown): value is ErrorMessageResponse {
        return !!value && typeof value === 'object' && 'errorcode' in value && 'errormessage' in value;
    }

    private bindContext() {
        if (!this.context) return;

        this.context.dynamicSpecialOrderHead$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (orderhead) => {
                this.orderhead = orderhead;
            },
        });

        this.context.coops$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.coops = value) });
        this.context.sellers$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.sellers = value) });
        this.context.customerPersons$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.customerPersons = value) });
        this.context.discount2s$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.specialDiscount2 = value) });
        this.context.stockplaces$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.stockplaces = value) });
        this.context.orderDialogs$.pipe(takeUntil(this.destroy$)).subscribe({ next: (value) => (this.dialog = value) });
    }

    private updateContextListKeys(fields: (keyof OrderHeadDynamicSpecialOrderHead)[]) {
        if (!this.context || !this.orderhead) return;

        if (fields.includes('stat_cust_no')) {
            this.context.setStatCustNo(this.orderhead.stat_cust_no);
        }

        if (fields.includes('stockplace_id')) {
            this.context.setStockplaceId(this.orderhead.stockplace_id);
        }
    }

    private toDynamicSpecialPostplace(postplace: PostPlaceItem): DynamicSpecialPostplaceItem {
        return {
            ...postplace,
            postplace_keyname: postplace.postplace_id_and_name,
        };
    }
}

export interface OrderHeadDynamicSpecialChangeEvent {
    /**
     * The field which was changed.
     */
    fields: (keyof OrderHeadDynamicSpecialOrderHead)[];

    /**
     * The current data in the component.
     */
    data: OrderHeadDynamicSpecialOrderHead;
}

export interface DynamicSpecialPostplaceItem extends PostPlaceItem {
    postplace_keyname: string;
}

// const testdata: OrderHeadGet = {
//     order_internal_no: '674200',
//     order_order_no: '635500',
//     order_offer_no: '',
//     edi_orderhead_keyno: '0',
//     orderhead_heading: 'Ordre 635500 Ekornes ASA',
//     stat_cust_no: '15230',
//     stat_cust_name: 'Ekornes ASA',
//     stat_cust_address: 'Postboks 22\r\nNO-6222 IKORNNES',
//     customers_orderno: '',
//     customers_ref1: '',
//     customers_ref2: '',
//     message_from_sales: '',
//     message_to_supplier: '',
//     stat_business_id: 'BENSIN',
//     stat_coop_id: 'wed aw',
//     stat_search: '',
//     stat_search_active: '0',
//     contact_pers_no: '0',
//     project_keyno: '0',
//     project_name: '',
//     serviceobject_keyno: '0',
//     serviceobject_name: '',
//     show_serviceobject_keyno: '1',
//     svea_payment_uri: '',
//     collection_keyno: '',
//     cust_note: '',
//     delivery_cust_no: '15230',
//     delivery_cust_name: 'Ekornes ASA',
//     delivery_cust_address: 'Ekornesveien 1b\nNO-6230 SYKKYLVEN',
//     label: '',
//     delivery_email: '',
//     deladronpurch: '0',
//     delivery_adr_note: '',
//     delivery_business_id: 'BENSIN',
//     delivery_coop_id: 'wed aw',
//     delivery_cust_unreg: '0',
//     delivery_adr_street: 'Ekornesveien 1b',
//     delivery_postplace_id_street: '6230',
//     delivery_postplace_name_street: 'SYKKYLVEN',
//     delivery_adr_mail: 'Postboks 22',
//     delivery_postplace_id: '6222',
//     delivery_postplace_name: 'IKORNNES',
//     delivery_cust_phone: '123',
//     delivery_country_id: 'no',
//     delivery_postplace_id_and_name: '6222 IKORNNES',
//     delivery_postplace_id_and_name_street: '6230 SYKKYLVEN',
//     invoice_cust_no: '15230',
//     invoice_cust_name: 'Ekornes ASA',
//     invoice_cust_address: 'Postboks 22\r\nNO-6222 IKORNNES',
//     invoice_business_id: 'BENSIN',
//     invoice_coop_id: 'wed aw',
//     invoice_country_id: 'no',
//     ordermethod_id: '1',
//     stockplace_id: 'LB',
//     seller_no: '1400',
//     businessco_no: '200',
//     purch_internal_no_from: '0',
//     orderstatus_name: 'Ikke levert',
//     order_date: '2025-10-01',
//     ordertype_id: '2',
//     ordertype_name: 'Ordre',
//     consernorder_internal_no: '0',
//     consernorder_order_no: '0',
//     data_changed_in_get: '0',
//     reg_by_portal_user_keyno: '142',
//     department_id: '',
//     orderhead_note1: '',
//     orderhead_note2: '',
//     orderhead_note3: '',
//     deliverydate_wish: '2025-10-01',
//     deliverydate_text: '',
//     deliverydate_confirmed: '2025-10-01',
//     deliverytime_confirmed: '00:00:00.000',
//     printed_orderconfirm: '1',
//     deliverymethod_no: '3',
//     pickjobgrp_keyno: '0',
//     show_pickjobgrp_keyno: '0',
//     delivery_place: '',
//     deliveryterm_no: '1',
//     orderpriority_keyno: '10',
//     freight_paid_by: '1',
//     freight_cost_ordval: '0.00',
//     freight_cost_ordval_agreed: '0.00',
//     payment_id: 'T2',
//     factoring_no: '1',
//     pricelist_id: '01',
//     pricelist_id2: '1',
//     purchase_pricelist_id: '',
//     purchase_pricelist_id2: '',
//     tax_keyno: '101',
//     invoicemethod_no: '1',
//     verified: '0',
//     order_valuta_id: 'NOK',
//     invoice_valuta_id: 'NOK',
//     offer_valid_to_date: '2025-10-31',
//     offer_sent: '0',
//     custbonusdeal: 'Bonus frie forhandlere',
//     partly_delivery_allowed: '0',
//     show_create_purchhead_interbranch: '0',
//     show_add_orderline: '1',
//     show_consignment_create: '0',
//     show_consignments_goto: '1',
//     show_invoice_make: '1',
//     show_setting: '1',
//     consignmentassembly_webpage_name: 'custactedit',
//     consignmentassembly_parms: '0/order_internal_no/674200',
//     consignmentassembly_custact_no: '0',
//     show_postcalc: '0',
//     nbr_of_documents: '13',
//     nbr_of_prepayments: '0',
//     nbr_of_invoices: '3',
//     show_orderpurch: '1',
//     show_button_forms: '1',
//     nbr_of_checklistdata: '0',
//     show_button_timesheetdiary: '1',
//     show_button_setnewdiscount: '0',
//     show_button_deleteorderlines: '1',
//     show_button_ohddispatches: '0',
//     show_button_duplicate_order: '1',
//     show_stocktransallocation: '0',
//     show_orderhead_capjobs: '1',
//     show_ordershipmentstatus: '0',
//     show_svea_prepayment: '0',
//     show_createcase: '0',
//     show_paymentplans: '0',
//     show_orderlinecredits: '0',
//     nbr_of_custacts: '0',
//     show_btn_consignment: '1',
//     show_btn_custact: '1',
//     show_btn_pre_payment: '0',
//     show_btn_invoices: '1',
//     show_btn_print: '1',
//     show_btn_email: '1',
//     show_btn_external_cost: '1',
//     show_jobcalc: '1',
//     nbr_of_jobcalcs: '0',
//     show_positions: '0',
//     nbr_of_positions: '1',
//     show_gb_orderline_displaytype: '0',
//     nbr_of_deviations: '0',
//     show_button_deviation: '1',
//     show_button_orderdialog: '1',
//     nbr_of_orderdialogs: '0',
//     show_button_documents: '1',
//     show_button_pictures: '1',
//     nbr_of_pictures: '0',
//     show_puch_order_purchaseorder: '0',
//     amount_total_topay: '375.36',
//     amount_payed: '0.00',
//     iscashpayment: '0',
//     show_orderline_textile: '0',
//     show_button_pickjobstatusconf_confirm: '0',
//     show_button_pickjobstatusconf_regretconfirm: '0',
//     show_button_pickjobstatusconf_pickjobgenerated: '0',
//     show_direct_order_delivery: '1',
//     gb_row_1: 'orderhead_dynamic_special',
//     show_customer_service: '0',
//     show_button_errorcheck_and_generate_bom: '0',
//     show_oh_head_stat_cust_no: '0',
//     show_oh_head_stat_cust_name: '0',
//     show_oh_head_stat_cust_address: '0',
//     show_oh_head_contact_pers_no: '0',
//     show_oh_head_customers_orderno: '0',
//     show_oh_head_project_name: '0',
//     show_oh_head_customers_ref1: '0',
//     show_oh_head_customers_ref2: '0',
//     show_oh_head_message_from_sales: '0',
//     show_oh_head_message_to_supplier: '0',
//     show_oh_head_stat_business_id: '0',
//     show_oh_head_stat_coop_id: '0',
//     show_oh_head_delivery_cust_unreg: '0',
//     show_oh_head_delivery_cust_no: '0',
//     show_oh_head_delivery_cust_name: '0',
//     show_oh_head_delivery_cust_address: '0',
//     show_oh_head_delivery_adr_street: '0',
//     show_oh_head_delivery_post_and_name_street: '0',
//     show_oh_head_delivery_adr_mail: '0',
//     show_oh_head_delivery_post_and_name: '0',
//     show_oh_head_delivery_country_id: '0',
//     show_oh_head_delivery_place: '0',
//     show_oh_head_label: '0',
//     show_oh_head_delivery_cust_phone: '0',
//     show_oh_head_delivery_email: '1',
//     show_oh_head_deladronpurch: '0',
//     show_oh_head_delivery_adr_note: '0',
//     show_oh_head_delivery_business_id: '0',
//     show_oh_head_delivery_coop_id: '0',
//     show_oh_head_invoice_cust_no: '0',
//     show_oh_head_invoice_cust_name: '0',
//     show_oh_head_invoice_cust_address: '0',
//     show_oh_head_invoice_country_id: '0',
//     show_oh_head_invoice_business_id: '0',
//     show_oh_head_invoice_coop_id: '0',
//     show_oh_head_businessco_no: '0',
//     show_oh_head_reg_by_portal_user_keyno: '0',
//     show_oh_head_ordertype_id: '0',
//     show_oh_head_order_date: '0',
//     show_oh_head_order_order_no: '0',
//     show_oh_head_orderstatus_name: '0',
//     show_oh_head_ordermethod_id: '0',
//     show_oh_head_stockplace_id: '0',
//     show_oh_head_seller_no: '0',
//     show_oh_head_department_id: '0',
//     show_oh_head_orderhead_note1: '1',
//     show_oh_head_orderhead_note2: '1',
//     show_oh_head_orderhead_note3: '1',
//     show_oh_head_deliverydate_wish: '0',
//     show_oh_head_deliverydate_text: '0',
//     show_oh_head_deliverydate_confirmed: '0',
//     show_oh_head_deliverytime_confirmed: '0',
//     show_oh_head_deliverymethod: '0',
//     show_oh_head_partly_delivery_allowed: '1',
//     show_oh_head_deliveryterm_no: '0',
//     show_oh_head_freight_paid_by: '0',
//     show_oh_head_freight_cost_ordval: '0',
//     show_oh_head_freight_cost_ordval_agreed: '0',
//     show_oh_head_custbonusdeal: '0',
//     show_oh_head_payment_id: '0',
//     show_oh_head_factoring_no: '0',
//     show_oh_head_pricelist_id: '0',
//     show_oh_head_pricelist_id2: '0',
//     show_oh_head_purchase_pricelist_id: '0',
//     show_oh_head_purchase_pricelist_id2: '0',
//     show_oh_head_tax_keyno: '0',
//     show_oh_head_invoicemethod_no: '0',
//     show_oh_head_order_valuta_id: '0',
//     show_oh_head_invoice_valuta_id: '0',
//     show_oh_head_collection: '0',
//     setting_orderlines_use_grid: '1',
//     setting_orderlines_use_new_grid: '1',
//     setting_orderlines_use_dynamic_grid: '1',
//     setting_oh_show_dynamic_special: '1',
//     setting_show_orderline_calculation: '0',
//     setting_enable_oh_delete: '1',
//     setting_enable_oh_cancel: '1',
//     carriers_paid_by_customer: '0',
//     orderconfirm_reportdef_keyno: '',
//     delivered: '0',
//     show_vertical_purchasing: '0',
//     show_orderform: '1',
//     show_tab_production: '1',
//     show_tab_delivery: '1',
//     show_tab_invoicing: '1',
//     show_tab_hours: '1',
//     gb_orderhead_hours_sub: 'TRANSFER_TO_ORDER',
//     show_tab_emails: '1',
//     show_tab_custactac: '1',
//     gb_orderhead_main_production_sub: 'POW',
//     show_tab_orderpicks: '1',
//     gb_orderhead_main_orderpicks_status: 'NOT_ADDED',
//     show_orderheadchoices: '1',
// };

// const orderheads = {
//     order_internal_no: 617700,
//     order_order_no: 578100,
//     order_offer_no: null,
//     orderhead_heading: 'Ordre 578100 Ålesund Bil AS',
//     stat_cust_no: 34983,
//     stat_cust_name: 'Ålesund Bil AS',
//     stat_cust_address: 'adresse 1\\r\\nadresse 2416\\r\\nadresse 3\\r\\nadresse 4\\r\\nNorway',
//     customers_orderno: '',
//     customers_ref1: '',
//     customers_ref2: '',
//     message_from_sales: '',
//     message_to_supplier: '',
//     stat_business_id: '',
//     stat_coop_id: '',
//     stat_search: '',
//     stat_search_active: '0',
//     contact_pers_no: 0,
//     project_keyno: 0,
//     project_name: '',
//     serviceobject_keyno: 0,
//     serviceobject_name: '',
//     svea_payment_uri: '',
//     delivery_cust_no: null,
//     delivery_cust_name: 'Ålesund Bil AS',
//     delivery_cust_address: null,
//     label: '',
//     delivery_email: '',
//     deladronpurch: '0',
//     delivery_adr_note: '',
//     delivery_business_id: '',
//     delivery_coop_id: '',
//     delivery_cust_unreg: '1',
//     delivery_adr_street: 'adresse 1',
//     delivery_postplace_id_street: '',
//     delivery_postplace_name_street: '',
//     delivery_adr_mail: '',
//     delivery_postplace_id: '',
//     delivery_postplace_name: null,
//     delivery_cust_phone: '',
//     delivery_country_id: 'ab',
//     delivery_postplace_id_and_name: ' ',
//     delivery_postplace_id_and_name_street: ' ',
//     invoice_cust_no: 34983,
//     invoice_cust_name: 'Ålesund Bil AS',
//     invoice_cust_address: 'adresse 1\\r\\nadresse 2416\\r\\nadresse 3\\r\\nadresse 4\\r\\nNorway',
//     invoice_business_id: '',
//     invoice_coop_id: '',
//     invoice_country_id: 'no',
//     ordermethod_id: '',
//     stockplace_id: 'LB',
//     seller_no: 0,
//     businessco_no: 200,
//     purch_internal_no_from: 0,
//     orderstatus_name: 'Ikke levert',
//     order_date: '2019-04-30',
//     ordertype_id: '2',
//     ordertype_name: 'Ordre',
//     consernorder_internal_no: 0,
//     consernorder_order_no: 0,
//     data_changed_in_get: '0',
//     reg_by_portal_user_keyno: 1,
//     department_id: '',
//     deliverydate_wish: null,
//     deliverydate_text: '',
//     deliverydate_confirmed: null,
//     deliverytime_confirmed: '00:00:00.000',
//     printed_orderconfirm: '0',
//     deliverymethod_no: 0,
//     pickjobgrp_keyno: 0,
//     delivery_place: '',
//     deliveryterm_no: 0,
//     orderpriority_keyno: 10,
//     freight_paid_by: '1',
//     freight_cost_ordval: 0,
//     freight_cost_ordval_agreed: 0,
//     payment_id: 'T2',
//     factoring_no: 0,
//     pricelist_id: '01',
//     pricelist_id2: '',
//     purchase_pricelist_id: '',
//     purchase_pricelist_id2: '',
//     tax_keyno: 101,
//     invoicemethod_no: 1,
//     order_valuta_id: 'NOK',
//     invoice_valuta_id: 'NOK',
//     offer_valid_to_date: '2025-10-02',
//     consignmentassembly_webpage_name: 'custactedit',
//     consignmentassembly_parms: '0/order_internal_no/617700',
//     consignmentassembly_custact_no: 0,
//     nbr_of_documents: 0,
//     nbr_of_prepayments: 0,
//     nbr_of_invoices: 0,
//     nbr_of_checklistdata: 0,
//     nbr_of_positions: 1,
//     nbr_of_deviations: 0,
//     nbr_of_orderdialogs: 1,
//     nbr_of_pictures: 0,
//     amount_total_topay: 5475,
//     amount_payed: 0,
//     iscashpayment: '0',
//     gb_row_1: 'orderhead_dynamic_special',
//     carriers_paid_by_customer: '1',
//     orderconfirm_reportdef_keyno: null,
//     delivered: '0',
//     delivery_cust_phone2: null,
//     special_discount2_keyno: null,
//     production_date: null,
//     tranportation_date: null,
//     production_date_confirmed: '0',
//     tranportation_date_confirmed: '0',
//     flexible_delivery_date: '0',
//     cust_contact_phone: null,
//     unmanned_contructionsite: '0',
//     message_to_driver: null,
//     contactperson: null,
//     deliver_to_customer_stock: '0',
//     customer_self_pickup: '0',
//     special_construction01: '0',
//     special_construction02: '0',
//     special_construction03: '0',
//     special_construction04: '0',
//     special_construction05: '0',
//     dlv_time_from: null,
//     dlv_time_to: null,
//     special_construction06: '0',
//     special_construction07: '0',
//     contact_pers_no2: null,
//     edi_description: null,
//     registered_date: '2019-04-30',
//     registered_time: '12:39:39.933',
//     modified_date: '2025-05-22',
//     modified_time: '16:25:45.140',
//     modifiedbyusername: 'Database-administrator',
//     regbyusername: 'Database-administrator',
//     amount_total: 4380,
//     cost_total: 0,
//     margin: 4380,
//     amount_total_pst: 1,
//     cost_total_pst: 0,
//     margin_pst: 1,
// };
